import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import Switch from 'react-switch';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import Utils from '../../utils/utils';
import AccessControl from '../../utils/access_control';
import { officeNameWithAliasesLabel } from '../../utils/office-option-label';
import { imputationDocumentId } from '../../pages/review-expense/utils/contract-expense-json';
import {
    SELECTION_CHIP_COLORS,
    selectionChipAvatarProps,
    selectionChipDefaultBadgeStyle,
    selectionChipListStyle,
    selectionChipRowStyle,
} from '../../utils/selection-chip-styles';

const readLabel = (t, key) => {
    const value = t.read(key);
    return typeof value === 'string' && value.length > 0 && value !== key ? value : '';
};

const userFormLabel = (t, key, fallbacks = []) => {
    const namespaced = readLabel(t, `client.users.${key}`);
    if (namespaced) {
        return namespaced;
    }
    for (const fallbackKey of fallbacks) {
        const value = readLabel(t, fallbackKey);
        if (value) {
            return value;
        }
    }
    if (process.env.NODE_ENV !== 'production') {
        return key;
    }
    return '';
};

const formatTimekeeperDisplayLabel = (timekeeper, item, mapTimekeeper) => {
    const mapped = mapTimekeeper(timekeeper);
    const rawLabel = mapped?.label ?? item?.index;
    if (rawLabel == null || rawLabel === '') {
        return '—';
    }
    return typeof rawLabel === 'string' ? rawLabel : String(rawLabel);
};

const homeOfficeSelectValue = (user, offices) => {
    const homeNorm = imputationDocumentId({ _id: user.home_office_id });
    if (!homeNorm) return null;
    const office = offices.find((o) => imputationDocumentId({ _id: o._id }) === homeNorm);
    return office
        ? {
            label: officeNameWithAliasesLabel(office),
            value: homeNorm,
        }
        : null;
};

const userRankSelectValue = (user, rankOptions) => {
    const rankId = user.rank?.id;
    if (rankId == null) {
        return null;
    }
    const fromOptions = rankOptions.find((option) => option.value === rankId);
    if (fromOptions) {
        return fromOptions;
    }
    return {
        value: rankId,
        label: user.rank?.description || String(rankId),
    };
};

function UserFormSection({ title, children }) {
    return (
        <div className="card p-3 mb-3">
            <h6 className="mb-3">{title}</h6>
            {children}
        </div>
    );
}

/**
 * Shared admin user form fields for create and edit modals.
 * mode: 'create' | 'edit' — password reset and vacation mode are edit-only.
 */
export function UserFormFields({
    data,
    user,
    setUser,
    mode,
    offices,
    availableRoles,
    adjointOptions,
    suppliers,
    setSuppliersFilterValue,
    styleSelect,
    adminUserSelectFieldWrapClass,
    userMap,
    timekeeperCache,
    loadTimekeepers,
    timekeeperMap,
    normalizeVacationMode,
    vacationDelegateOptions,
    selfApprovalSubstituteOptions = [],
    loadCards,
    originalUser,
    onResetPassword,
    userToResetPassword,
    hasAderantIntegration = false,
    isEliteClient = false,
    rankOptions = [],
}) {
    const [timekeeperAddSelectKey, setTimekeeperAddSelectKey] = useState(0);
    const [adjointAddSelectKey, setAdjointAddSelectKey] = useState(0);
    const isEditMode = mode === 'edit';
    const showVacationFields = isEditMode && ['Approver', 'Admin'].includes(user.role);
    const vacationEnabled = Boolean(user.vacation_mode?.enabled);
    const adjoints = user.adjoints ?? [];
    const timekeepers = user.timekeepers ?? [];
    const emailNotificationsEnabled = !user.is_email_notification_disabled;
    const addAdjointPlaceholder = userFormLabel(data.t, 'add_adjoint_placeholder', ['adjoints'])
        || `${readLabel(data.t, 'add')} ${readLabel(data.t, 'adjoints')}`.trim();
    const timekeepersLabel = userFormLabel(data.t, 'timekeepers', ['timekeeper', 'time_keepers'])
        || readLabel(data.t, 'timekeeper');
    const addTimekeeperPlaceholder = userFormLabel(data.t, 'add_timekeeper_placeholder', ['add_timekeeper'])
        || `${readLabel(data.t, 'add')} ${readLabel(data.t, 'timekeeper')}`.trim();
    const setTimekeeperDefaultLabel = userFormLabel(data.t, 'set_timekeeper_default', ['default']);
    const emailNotificationsLabel = userFormLabel(data.t, 'email_notifications', ['email_notification'])
        || readLabel(data.t, 'rule.email');
    const showSelfApprovalSubstituteField = ['Approver', 'Admin'].includes(user.role);

    const availableAdjointOptions = adjointOptions
        .filter((option) => !adjoints.includes(option.id_user))
        .map(userMap);

    const addAdjoint = (choice) => {
        if (choice?.value == null || choice.value === '') {
            return;
        }
        if (adjoints.includes(choice.value)) {
            return;
        }
        setUser({
            ...user,
            adjoints: [...adjoints, choice.value],
        });
        setAdjointAddSelectKey((key) => key + 1);
    };

    const removeAdjoint = (index) => {
        const nextAdjoints = adjoints.filter((_adjointId, itemIndex) => itemIndex !== index);
        // Always keep adjoints as an array in the PUT payload so clearing the list
        // persists via $set (omitting the key would leave stale DB values).
        setUser({
            ...user,
            adjoints: nextAdjoints,
        });
    };

    const addTimekeeper = (choice) => {
        if (!choice?.value?.index) {
            return;
        }
        const alreadyAssigned = timekeepers.some((item) => item.index === choice.value.index);
        if (alreadyAssigned) {
            return;
        }
        setUser({
            ...user,
            timekeepers: [
                ...timekeepers,
                { index: choice.value.index, is_default: timekeepers.length === 0 },
            ],
        });
        setTimekeeperAddSelectKey((key) => key + 1);
    };

    const removeTimekeeper = (index) => {
        const nextTimekeepers = user.timekeepers.filter((_item, itemIndex) => itemIndex !== index);
        if (nextTimekeepers.length > 0 && !nextTimekeepers.some((item) => item.is_default)) {
            nextTimekeepers[0] = { ...nextTimekeepers[0], is_default: true };
        }
        setUser({ ...user, timekeepers: nextTimekeepers });
    };

    const setDefaultTimekeeper = (index) => {
        setUser({
            ...user,
            timekeepers: user.timekeepers.map((item, itemIndex) => ({
                ...item,
                is_default: itemIndex === index,
            })),
        });
    };

    return (
        <>
            <UserFormSection title={userFormLabel(data.t, 'form_section_identity', ['user'])}>
                <div className="form-group">
                    <label>{data.t.read('email')}</label>
                    <input
                        type="email"
                        name="email"
                        className="form-control"
                        value={user.email}
                        onChange={(event) => {
                            setUser({ ...user, email: event.target.value });
                        }}
                        required
                        placeholder={data.t.read('email')}
                    />
                </div>
                <div className="form-group">
                    <label>{data.t.read('first_name')}</label>
                    <input
                        type="text"
                        name="first_name"
                        className="form-control"
                        value={user.first_name}
                        onChange={(event) => {
                            setUser({ ...user, first_name: event.target.value });
                        }}
                        required
                        placeholder={data.t.read('first_name')}
                    />
                </div>
                <div className="form-group">
                    <label>{data.t.read('last_name')}</label>
                    <input
                        type="text"
                        name="last_name"
                        className="form-control"
                        value={user.last_name}
                        onChange={(event) => {
                            setUser({ ...user, last_name: event.target.value });
                        }}
                        required
                        placeholder={data.t.read('last_name')}
                    />
                </div>
                <div className="form-group mb-0">
                    <label>{data.t.read('client_table.role')}</label>
                    <select
                        name="role"
                        className="form-control"
                        value={user.role}
                        onChange={(event) => {
                            setUser({ ...user, role: event.target.value });
                        }}
                        required
                    >
                        {availableRoles.map((role) => (
                            <option key={role} value={role}>
                                {data?.t?.read(`roles.${role.toLowerCase()}`) || role}
                            </option>
                        ))}
                    </select>
                </div>
            </UserFormSection>

            {showVacationFields && (
                <UserFormSection title={data.t.read('vacation_mode')}>
                    <p className="text-muted small mb-3">{data.t.read('vacation_mode_admin_help')}</p>
                    <div className="form-group d-flex align-items-center mb-0">
                        <input
                            id="admin-vacation-mode-enabled-edit"
                            type="checkbox"
                            className="mr-2"
                            checked={vacationEnabled}
                            onChange={(event) => setUser({
                                ...user,
                                vacation_mode: {
                                    ...normalizeVacationMode(user.vacation_mode),
                                    enabled: event.target.checked,
                                    assigned_by: {
                                        id_user: data.id_user,
                                        first_name: data.firstName,
                                        last_name: data.lastName,
                                    },
                                },
                            })}
                        />
                        <label className="mb-0" htmlFor="admin-vacation-mode-enabled-edit">
                            {data.t.read('vacation_mode_enabled')}
                        </label>
                    </div>
                    {vacationEnabled && (
                        <>
                            <div className="form-group mt-3">
                                <label>{data.t.read('vacation_delegate')}</label>
                                <div className={adminUserSelectFieldWrapClass}>
                                    <Select
                                        classNamePrefix="select"
                                        isClearable
                                        styles={styleSelect}
                                        options={vacationDelegateOptions}
                                        value={vacationDelegateOptions.find(
                                            (option) => option.raw.id_user === user.vacation_mode?.delegate_approver?.id_user
                                        ) ?? null}
                                        onChange={(option) => setUser({
                                            ...user,
                                            vacation_mode: {
                                                ...normalizeVacationMode(user.vacation_mode),
                                                delegate_approver: option?.raw ?? null,
                                                assigned_by: {
                                                    id_user: data.id_user,
                                                    first_name: data.firstName,
                                                    last_name: data.lastName,
                                                },
                                            },
                                        })}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>{data.t.read('vacation_start_date')}</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={user.vacation_mode?.start_date ?? ''}
                                    onChange={(event) => setUser({
                                        ...user,
                                        vacation_mode: {
                                            ...normalizeVacationMode(user.vacation_mode),
                                            start_date: event.target.value,
                                            assigned_by: {
                                                id_user: data.id_user,
                                                first_name: data.firstName,
                                                last_name: data.lastName,
                                            },
                                        },
                                    })}
                                />
                            </div>
                            <div className="form-group mb-0">
                                <label>{data.t.read('vacation_end_date')}</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={user.vacation_mode?.end_date ?? ''}
                                    onChange={(event) => setUser({
                                        ...user,
                                        vacation_mode: {
                                            ...normalizeVacationMode(user.vacation_mode),
                                            end_date: event.target.value,
                                            assigned_by: {
                                                id_user: data.id_user,
                                                first_name: data.firstName,
                                                last_name: data.lastName,
                                            },
                                        },
                                    })}
                                />
                            </div>
                        </>
                    )}
                </UserFormSection>
            )}

            {showSelfApprovalSubstituteField && (
                <UserFormSection title={userFormLabel(data.t, 'self_approval_substitute_section')}>
                    <p className="text-muted small mb-3">
                        {userFormLabel(data.t, 'self_approval_substitute_help')}
                    </p>
                    <div className="form-group mb-0">
                        <label>{userFormLabel(data.t, 'self_approval_substitute_approver')}</label>
                        <div className={adminUserSelectFieldWrapClass}>
                            <Select
                                name="self_approval_substitute_approver"
                                classNamePrefix="select"
                                isClearable
                                styles={styleSelect}
                                options={selfApprovalSubstituteOptions}
                                value={selfApprovalSubstituteOptions.find(
                                    (option) => option.raw.id_user === user.self_approval_substitute_approver?.id_user
                                ) ?? null}
                                onChange={(option) => setUser({
                                    ...user,
                                    self_approval_substitute_approver: option?.raw ?? null,
                                })}
                            />
                        </div>
                    </div>
                </UserFormSection>
            )}

            <UserFormSection title={userFormLabel(data.t, 'form_section_assignments', ['adjoints'])}>
                <div className="form-group">
                    <label>{data.t.read('adjoints')}</label>
                    <div className={adminUserSelectFieldWrapClass}>
                        <Select
                            key={adjointAddSelectKey}
                            name="adjoint_add"
                            styles={styleSelect}
                            classNamePrefix="select"
                            value={null}
                            placeholder={addAdjointPlaceholder}
                            options={availableAdjointOptions}
                            onChange={addAdjoint}
                            isClearable
                        />
                    </div>
                    {adjoints.length === 0 ? (
                        <p className="text-muted small mb-0 mt-2">
                            {userFormLabel(data.t, 'no_adjoints_assigned')}
                        </p>
                    ) : (
                        <div
                            style={selectionChipListStyle()}
                            data-testid="admin-user-adjoints-list"
                        >
                            {adjoints.map((adjointId, index) => {
                                const adjointLabel = userMap(adjointId)?.label || String(adjointId);
                                const avatar = selectionChipAvatarProps(adjointLabel);
                                return (
                                    <div
                                        key={`${adjointId}-${index}`}
                                        style={selectionChipRowStyle()}
                                    >
                                        <div className="d-flex align-items-center flex-wrap">
                                            <div style={avatar.style}>{avatar.initial}</div>
                                            <span style={{ color: SELECTION_CHIP_COLORS.label }}>
                                                {adjointLabel}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm ml-2 flex-shrink-0"
                                            onClick={() => removeAdjoint(index)}
                                        >
                                            {data.t.read('delete')}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="form-group">
                    <label>{timekeepersLabel}</label>
                    <div className={adminUserSelectFieldWrapClass}>
                        <AsyncSelect
                            key={timekeeperAddSelectKey}
                            name="timekeeper_add"
                            styles={styleSelect}
                            classNamePrefix="select"
                            value={null}
                            placeholder={addTimekeeperPlaceholder}
                            onChange={addTimekeeper}
                            cacheOptions
                            loadOptions={loadTimekeepers}
                            defaultOptions
                            isClearable
                        />
                    </div>
                    {timekeepers.length === 0 ? (
                        <p className="text-muted small mb-0 mt-2">
                            {userFormLabel(data.t, 'no_timekeepers_assigned')}
                        </p>
                    ) : (
                        <div
                            style={selectionChipListStyle()}
                            data-testid="admin-user-timekeepers-list"
                        >
                            {
                                [...timekeepers]
                                    .map((item, index) => ({ item, index }))
                                    .sort((a, b) => b.item.is_default - a.item.is_default)
                                    .map(({ item, index }) => {
                                        const timekeeper = timekeeperCache[item?.index] || null;
                                        const timekeeperLabel = formatTimekeeperDisplayLabel(
                                            timekeeper,
                                            item,
                                            timekeeperMap
                                        );
                                        const avatar = selectionChipAvatarProps(timekeeperLabel);
                                        const defaultLabel = userFormLabel(
                                            data.t,
                                            'timekeeper_default',
                                            ['client_table.default']
                                        );
                                        return (
                                            <div
                                                key={`${item.index}-${index}`}
                                                style={selectionChipRowStyle()}
                                            >
                                                <div className="d-flex align-items-center flex-wrap">
                                                    <div style={avatar.style}>{avatar.initial}</div>
                                                    <span style={{ color: SELECTION_CHIP_COLORS.label }}>
                                                        {timekeeperLabel}
                                                    </span>
                                                    {item.is_default ? (
                                                        <span style={selectionChipDefaultBadgeStyle()}>
                                                            {defaultLabel}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                <div className="d-flex align-items-center ml-2 flex-shrink-0">
                                                    {!item.is_default ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary btn-sm"
                                                            title={setTimekeeperDefaultLabel}
                                                            aria-label={setTimekeeperDefaultLabel}
                                                            onClick={() => setDefaultTimekeeper(index)}
                                                        >
                                                            {setTimekeeperDefaultLabel}
                                                        </button>
                                                    ) : null}
                                                    <button
                                                        type="button"
                                                        className={`btn btn-outline-secondary btn-sm${!item.is_default ? ' ml-2' : ''}`}
                                                        onClick={() => removeTimekeeper(index)}
                                                    >
                                                        {data.t.read('delete')}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                            }
                        </div>
                    )}
                </div>
                <div className="form-group mb-0">
                    <label>{data.t.read('supplierName')}</label>
                    <div className={adminUserSelectFieldWrapClass}>
                        <Select
                            name="supplier"
                            isClearable
                            value={user.supplier ? { value: user.supplier, label: user.supplier['names']?.[0] } : null}
                            options={suppliers.map((s) => ({ label: s['names']?.[0], value: s }))}
                            styles={styleSelect}
                            classNamePrefix="select"
                            placeholder={data.t.read('supplierName')}
                            onInputChange={(value, { action }) => {
                                if (action === 'input-change') {
                                    setSuppliersFilterValue(value);
                                }
                            }}
                            onChange={(s) => setUser({
                                ...user,
                                supplier: s
                                    ? { supplier_id: s.value._id.$oid, names: s.value.names }
                                    : null,
                            })}
                        />
                    </div>
                </div>
            </UserFormSection>

            <UserFormSection title={userFormLabel(data.t, 'form_section_organization', ['home_office'])}>
                <div className="form-group">
                    <label>{data.t.read('home_office')}</label>
                    <div className={adminUserSelectFieldWrapClass}>
                        <Select
                            name="home_office_id"
                            isClearable
                            options={offices.map((o) => ({
                                label: officeNameWithAliasesLabel(o),
                                value: imputationDocumentId({ _id: o._id }),
                            }))}
                            value={homeOfficeSelectValue(user, offices)}
                            styles={styleSelect}
                            classNamePrefix="select"
                            placeholder={data.t.read('home_office')}
                            onChange={(option) => setUser({ ...user, home_office_id: option?.value ?? null })}
                        />
                    </div>
                </div>
                {isEliteClient ? (
                    <div className="form-group">
                        <label>{data.t.read('section')}</label>
                        {/* The sync rewrites it from Elite on every run. */}
                        <input
                            type="text"
                            name="section"
                            className="form-control"
                            disabled
                            value={user.section || ''}
                            placeholder={data.t.read('section')}
                        />
                    </div>
                ) : null}
                {hasAderantIntegration ? (
                    <div className="form-group">
                        <label>{userFormLabel(data.t, 'rank', ['imputation_ranks'])}</label>
                        <div className={adminUserSelectFieldWrapClass}>
                            <Select
                                name="rank"
                                isClearable
                                options={rankOptions}
                                value={userRankSelectValue(user, rankOptions)}
                                styles={styleSelect}
                                classNamePrefix="select"
                                placeholder={userFormLabel(data.t, 'rank_placeholder', ['imputation_ranks_placeholder'])}
                                onChange={(option) => setUser({
                                    ...user,
                                    rank: option
                                        ? { id: option.value, description: option.label }
                                        : null,
                                })}
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                menuPosition="fixed"
                            />
                        </div>
                    </div>
                ) : null}
                <div className="form-group mb-0">
                    <label>{data.t.read('c-card')}</label>
                    <div className={adminUserSelectFieldWrapClass}>
                        <AsyncSelect
                            name="card"
                            value={user.cards ?? null}
                            defaultOptions
                            cacheOptions
                            loadOptions={loadCards}
                            placeholder={data.t.read('c-card')}
                            isMulti
                            isClearable
                            onChange={(value) => setUser({ ...user, cards: value })}
                            styles={styleSelect}
                            classNamePrefix="select"
                        />
                    </div>
                </div>
            </UserFormSection>

            <UserFormSection title={userFormLabel(data.t, 'form_section_account', ['active'])}>
                <div className="form-group d-flex align-items-center">
                    <div className="form-label col-4 pl-0 mb-0">{emailNotificationsLabel}</div>
                    <div className="p-0">
                        <Switch
                            className="switch d-block mt-0 ml-2"
                            checked={emailNotificationsEnabled}
                            width={70}
                            onColor="#F39800"
                            onHandleColor="#2693e6"
                            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                            activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                            onChange={(enabled) => {
                                setUser({
                                    ...user,
                                    is_email_notification_disabled: !enabled,
                                });
                            }}
                        />
                    </div>
                </div>
                <div className="form-group d-flex align-items-center">
                    <div className="form-label col-4 pl-0 mb-0">{data.t.read('active')}</div>
                    <div className="p-0">
                        <Switch
                            className="switch d-block mt-0 ml-2"
                            checked={user.active !== false}
                            disabled={
                                isEditMode
                                && !(Utils.userCanModify(data, user) || AccessControl.userCanModify(data, user))
                            }
                            width={70}
                            onColor="#F39800"
                            onHandleColor="#2693e6"
                            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                            activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                            onChange={(active) => {
                                if (
                                    !isEditMode
                                    || Utils.userCanModify(data, user)
                                    || AccessControl.userCanModify(data, user)
                                ) {
                                    setUser({ ...user, active });
                                }
                            }}
                        />
                    </div>
                </div>
                {isEditMode && (
                    <div className="form-group d-flex align-items-center mb-0">
                        <div className="form-label col-4 pl-0 mb-0">{data.t.read('password')}</div>
                        <div className="p-0">
                            <button
                                className="btn btn-primary admin-user-reset-toggle-btn ml-2"
                                type="button"
                                onClick={() => onResetPassword(originalUser)}
                            >
                                <FontAwesomeIcon
                                    className={
                                        ((originalUser._id === userToResetPassword?._id) && userToResetPassword?.is_generating)
                                            ? 'infinite-rotate'
                                            : 'mr-1'
                                    }
                                    icon={faCog}
                                />
                                <span>{data.t.read('reset')}</span>
                            </button>
                        </div>
                    </div>
                )}
            </UserFormSection>
        </>
    );
}
